import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';

export type TAuthPreset = components['schemas']['AuthPresetResDto'];
export type TAuthPresetVersion = components['schemas']['AuthPresetVersionResDto'];
export type TAuthPresetSpec = components['schemas']['AuthPresetSpecDto'];
export type TAuthPresetSignature = components['schemas']['AuthPresetSignatureDto'];
export type TAuthPresetTimestamp = components['schemas']['AuthPresetTimestampDto'];
export type TSignatureParse = TAuthPresetSignature['location']['parse'];
export type TTimestampLocation = TAuthPresetTimestamp['location'];
export type TAuthPresetUsage = components['schemas']['AuthPresetUsageResDto'];
export type TAuthPresetSample = components['schemas']['AuthPresetSampleResDto'];
export type TVerifyResult = components['schemas']['VerifyAuthPresetResDto'];
export type TVerifyHeaderField = components['schemas']['VerifyHeaderFieldDto'];
export type TApplyResult = components['schemas']['ApplyAuthPresetVersionResDto'];
export type TPutSample = components['schemas']['PutAuthPresetSampleReqDto'];
export type TVerifyDraftReq = components['schemas']['VerifyAuthPresetDraftReqDto'];
export type TSenderSearch = components['schemas']['AuthPresetSearchResDto'];
export type TSender = components['schemas']['AuthPresetSearchItemResDto'];
export type TSenderSignature = components['schemas']['AuthPresetSignatureShapeResDto'];
export type TSenderNoMatch = components['schemas']['AuthPresetSearchNoMatchResDto'];
export type TSenderMatch = NonNullable<TSender['matchedOn']>;
export type TAlgorithm = TAuthPresetSignature['algorithm'];
export type TSignatureEncoding = TAuthPresetSignature['encoding'];
export type TSecretEncoding = TAuthPresetSignature['secretEncoding'];
export type TTimestampFormat = NonNullable<TAuthPresetTimestamp['format']>;
export type TFailedStep = NonNullable<TVerifyResult['failedStep']>;

export const listPresets = (): Promise<{ data: TAuthPreset[] }> =>
  unwrap(api.GET('/v1/auth-presets', { params: { query: { limit: 100, withTotal: true } } }));

export const searchSenders = (q: string, limit: number): Promise<TSenderSearch> =>
  unwrap(api.GET('/v1/auth-presets/search', { params: { query: { ...(q ? { q } : {}), limit } } }));

export const getPreset = (id: string): Promise<TAuthPreset> =>
  unwrap(api.GET('/v1/auth-presets/{id}', { params: { path: { id } } }));

export const listPresetVersions = (id: string): Promise<TAuthPresetVersion[]> =>
  unwrap(api.GET('/v1/auth-presets/{id}/versions', { params: { path: { id } } }));

export const getPresetVersion = (id: string, version: number): Promise<TAuthPresetVersion> =>
  unwrap(
    api.GET('/v1/auth-presets/{id}/versions/{version}', { params: { path: { id, version } } }),
  );

export const presetUsage = (id: string): Promise<TAuthPresetUsage[]> =>
  unwrap(api.GET('/v1/auth-presets/{id}/usage', { params: { path: { id } } }));

export const createPreset = (body: {
  key: string;
  name: string;
  description?: string;
  spec: TAuthPresetSpec;
  comment?: string;
}): Promise<TAuthPresetVersion> => unwrap(api.POST('/v1/auth-presets', { body }));

export const createPresetVersion = (
  id: string,
  spec: TAuthPresetSpec,
  comment?: string,
): Promise<TAuthPresetVersion> =>
  unwrap(
    api.POST('/v1/auth-presets/{id}/versions', {
      params: { path: { id } },
      body: { spec, ...(comment ? { comment } : {}) },
    }),
  );

export const duplicatePreset = (
  id: string,
  body: { key?: string; name?: string },
): Promise<TAuthPreset> =>
  unwrap(api.POST('/v1/auth-presets/{id}/duplicate', { params: { path: { id } }, body }));

export const importPreset = (body: {
  spec: TAuthPresetSpec;
  key?: string;
  name?: string;
}): Promise<TAuthPreset> => unwrap(api.POST('/v1/auth-presets/import', { body }));

export const exportPresetVersion = (id: string, version: number): Promise<TAuthPresetSpec> =>
  unwrap(
    api.GET('/v1/auth-presets/{id}/versions/{version}/export', {
      params: { path: { id, version } },
    }),
  );

export const deletePreset = async (id: string): Promise<void> => {
  await unwrap(api.DELETE('/v1/auth-presets/{id}', { params: { path: { id } } }));
};

export const updatePresetMetadata = (
  id: string,
  body: { name?: string; description?: string | null },
): Promise<TAuthPreset> =>
  unwrap(api.PATCH('/v1/auth-presets/{id}', { params: { path: { id } }, body }));

// Without `secret` the answer is `signatureMatched: null`, which is not "no match".
export const verifyDraft = (body: TVerifyDraftReq): Promise<TVerifyResult> =>
  unwrap(api.POST('/v1/auth-presets/verify', { body }));

export const getSample = (id: string): Promise<TAuthPresetSample> =>
  unwrap(api.GET('/v1/auth-presets/{id}/sample', { params: { path: { id } } }));

export const putSample = (id: string, body: TPutSample): Promise<TAuthPreset> =>
  unwrap(api.PUT('/v1/auth-presets/{id}/sample', { params: { path: { id } }, body }));

export const clearSample = (id: string): Promise<TAuthPreset> =>
  unwrap(api.DELETE('/v1/auth-presets/{id}/sample', { params: { path: { id } } }));

export const checkSample = (id: string, version: number, secret?: string): Promise<TVerifyResult> =>
  unwrap(
    api.POST('/v1/auth-presets/{id}/versions/{version}/check-sample', {
      params: { path: { id, version } },
      body: secret ? { secret } : {},
    }),
  );

export const applyVersion = (
  id: string,
  version: number,
  body: {
    sourceAuthMethodIds: string[];
    secret?: string;
    acknowledgeSampleMismatch: boolean;
  },
): Promise<TApplyResult> =>
  unwrap(
    api.POST('/v1/auth-presets/{id}/versions/{version}/apply', {
      params: { path: { id, version } },
      body,
    }),
  );
